class RMSProductDetails {
  constructor(root) {
    this.root = root;
    this.enhance();
  }

  enhance() {
    if (this.root.dataset.rmsEnhanced === 'true') return;

    this.root.classList.add('rms-product-description');
    this.root.dataset.rmsProductDetails = '';
    if (!this.root.hasAttribute('data-open-first')) this.root.dataset.openFirst = 'false';

    const directChildren = Array.from(this.root.children);
    const levelTwoHeadings = directChildren.filter((node) => node.tagName === 'H2');
    const headings = levelTwoHeadings.length > 1
      ? levelTwoHeadings
      : directChildren.filter((node) => node.tagName === 'H3');

    if (headings.length < 2) return;

    const firstHeading = headings[0];
    const firstHeadingIndex = directChildren.indexOf(firstHeading);
    const startsWithEditorialTitle = firstHeadingIndex <= 1 && levelTwoHeadings.length > 1;
    const sectionHeadings = startsWithEditorialTitle ? headings.slice(1) : headings;

    sectionHeadings.forEach((heading, index) => {
      const details = document.createElement('details');
      details.className = 'rms-product-detail';

      const summary = document.createElement('summary');
      summary.className = 'rms-product-detail__summary';
      summary.innerHTML = `<span>${heading.innerHTML}</span><span class="rms-product-detail__icon" aria-hidden="true"></span>`;

      const content = document.createElement('div');
      content.className = 'rms-product-detail__content';

      let sibling = heading.nextSibling;
      while (sibling && !sectionHeadings.includes(sibling)) {
        const nextSibling = sibling.nextSibling;
        content.appendChild(sibling);
        sibling = nextSibling;
      }

      details.append(summary, content);
      heading.replaceWith(details);

      if (index === 0 && this.root.dataset.openFirst === 'true') details.open = true;
    });

    this.root.dataset.rmsEnhanced = 'true';
  }
}

const enhanceRMSSku = (scope = document) => {
  scope.querySelectorAll('.product__sku').forEach((sku) => {
    sku.classList.add('rms-product__sku');
    const label = sku.querySelector('.visually-hidden');
    if (label) {
      label.classList.remove('visually-hidden');
      label.classList.add('rms-product__sku-label');
    }
  });
};

const enhanceRMSProductDetails = (scope = document) => {
  const roots = new Set([
    ...scope.querySelectorAll('[data-rms-product-details]'),
    ...scope.querySelectorAll('.product__description.rte.quick-add-hidden')
  ]);
  roots.forEach((root) => new RMSProductDetails(root));
  enhanceRMSSku(scope);
};

const RMS_STREAMING_BASE_URL = 'https://streaming.referencemusicstore.com/app/release/';

const RMS_FORMAT_FAMILIES = [
  { id: 'vinyl', label: 'VINYL' },
  { id: 'disc', label: 'CD / SACD' },
  { id: 'digital', label: 'DIGITAL MASTER' },
  { id: 'streaming', label: 'STREAMING' }
];

const RMS_UI_COPY = {
  en: {
    availableFormats: 'Available formats',
    availableFormat: 'Available format',
    soldOut: 'Sold out',
    listen: 'Listen on REFERENCE MUSIC STREAMING',
    streamingNote: 'Open this release in Reference Music Streaming.'
  },
  it: {
    availableFormats: 'Formati disponibili',
    availableFormat: 'Formato disponibile',
    soldOut: 'Esaurito',
    listen: 'Ascolta su REFERENCE MUSIC STREAMING',
    streamingNote: 'Apri questa pubblicazione in Reference Music Streaming.'
  },
  de: {
    availableFormats: 'Verfügbare Formate',
    availableFormat: 'Verfügbares Format',
    soldOut: 'Ausverkauft',
    listen: 'Auf REFERENCE MUSIC STREAMING anhören',
    streamingNote: 'Diese Veröffentlichung in Reference Music Streaming öffnen.'
  },
  es: {
    availableFormats: 'Formatos disponibles',
    availableFormat: 'Formato disponible',
    soldOut: 'Agotado',
    listen: 'Escuchar en REFERENCE MUSIC STREAMING',
    streamingNote: 'Abrir este lanzamiento en Reference Music Streaming.'
  }
};

const getRMSCopy = () => {
  const language = (document.documentElement.lang || 'en').toLowerCase().split('-')[0];
  return RMS_UI_COPY[language] || RMS_UI_COPY.en;
};

const escapeRMSHtml = (value = '') => String(value)
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#039;');

const getRMSFormatFamily = (value = '') => {
  const normalized = value.toLowerCase().replaceAll('–', '-').replaceAll('—', '-');

  if (
    normalized.includes('file ') ||
    normalized.includes('hi-res') ||
    normalized.includes('hi res') ||
    normalized.includes('pcm ') ||
    normalized.includes('dsd') ||
    normalized.includes('dxd') ||
    normalized.includes('flac') ||
    normalized.includes('wav') ||
    normalized.includes('khz')
  ) return 'digital';

  if (
    normalized.includes('sacd') ||
    normalized.includes('masterclone') ||
    normalized.includes('black cd') ||
    normalized.includes('gold cd') ||
    normalized === 'cd' ||
    normalized.startsWith('cd ')
  ) return 'disc';

  if (
    normalized.includes('vinyl') ||
    normalized === 'lp' ||
    normalized.startsWith('lp ') ||
    normalized.includes('180 g') ||
    normalized.includes('180g')
  ) return 'vinyl';

  return null;
};

const getRMSFormatLabel = (value = '') => value
  .replace(/^File\s+/i, '')
  .replace(/\s+[—-]\s+CD Resolution$/i, '')
  .trim();

const getRMSFormatMeta = (value = '', family = '') => {
  const normalized = value.toLowerCase();

  if (family === 'vinyl') {
    if (normalized.includes('crystal')) return 'Vinyl · Crystal';
    if (normalized.includes('dmm')) return 'Vinyl · DMM';
    return 'Vinyl';
  }

  if (family === 'disc') {
    if (normalized.includes('sacd')) return 'Super Audio CD';
    if (normalized.includes('masterclone')) return 'Masterclone CD';
    if (normalized.includes('gold')) return '24K Gold CD';
    return 'Compact Disc';
  }

  if (family === 'digital') {
    if (normalized.includes('dsd256')) return 'DSD · 11.2 MHz';
    if (normalized.includes('dsd128')) return 'DSD · 5.6 MHz';
    if (normalized.includes('dsd64') || normalized.includes('dsd 64')) return 'DSD · 2.8 MHz';
    if (normalized.includes('dxd')) return 'PCM · DXD';
    if (normalized.includes('flac')) return normalized.includes('44.1') ? 'FLAC · CD Resolution' : 'FLAC · Hi-Res';
    if (normalized.includes('44.1') && (normalized.includes('16bit') || normalized.includes('16 bit'))) return 'PCM · CD Resolution';
    if (normalized.includes('hi-res') || normalized.includes('hi res')) return 'PCM · Hi-Res';
    return 'Digital Master';
  }

  return '';
};

const getRMSVariantData = (productScope) => {
  const script = productScope.querySelector('script[data-rms-variant-data]');
  if (!script) return { variants: [], streamingRelease: '' };

  let variants = [];
  try {
    variants = JSON.parse(script.textContent || '[]');
  } catch (error) {
    console.warn('RMS variant metadata could not be parsed.', error);
  }

  return {
    variants: Array.isArray(variants) ? variants : [],
    streamingRelease: (script.dataset.rmsStreamingRelease || '').trim()
  };
};

const getRMSNativeOptionValues = (productScope) => {
  const variantSelects = productScope.querySelector('variant-selects');
  if (!variantSelects) return null;

  const fieldsets = Array.from(variantSelects.querySelectorAll('fieldset'));
  const selects = Array.from(variantSelects.querySelectorAll('select'));
  const optionGroupCount = fieldsets.length + selects.length;
  if (optionGroupCount !== 1) return null;

  const radios = Array.from(variantSelects.querySelectorAll('fieldset input[type="radio"]'));
  if (radios.length) {
    return radios.map((input) => ({ value: input.value, selected: input.checked }));
  }

  const select = selects[0];
  if (!select) return null;
  return Array.from(select.options).map((option) => ({ value: option.value, selected: option.selected }));
};

const getRMSNativeChoices = (productScope) => {
  const nativeValues = getRMSNativeOptionValues(productScope);
  if (!nativeValues || !nativeValues.length) return null;

  const { variants } = getRMSVariantData(productScope);
  const metadataByOption = new Map(
    variants
      .filter((variant) => variant && typeof variant.optionValue === 'string')
      .map((variant) => [variant.optionValue, variant])
  );

  const choices = nativeValues.map((nativeChoice) => {
    const family = getRMSFormatFamily(nativeChoice.value);
    const metadata = metadataByOption.get(nativeChoice.value);

    return {
      value: nativeChoice.value,
      selected: nativeChoice.selected,
      family,
      unavailable: metadata ? metadata.available === false : false,
      price: metadata?.price || '',
      sku: metadata?.sku || ''
    };
  });

  if (choices.some((choice) => !choice.family)) return null;
  return choices;
};

const buildRMSFormatGroups = (productScope) => {
  const choices = getRMSNativeChoices(productScope);
  if (!choices) return [];

  const grouped = new Map();
  choices.forEach((choice) => {
    if (!grouped.has(choice.family)) grouped.set(choice.family, []);
    grouped.get(choice.family).push(choice);
  });

  const groups = RMS_FORMAT_FAMILIES
    .filter((family) => family.id !== 'streaming' && grouped.has(family.id))
    .map((family) => ({ ...family, choices: grouped.get(family.id) }));

  const { streamingRelease } = getRMSVariantData(productScope);
  if (streamingRelease) {
    const streamingFamily = RMS_FORMAT_FAMILIES.find((family) => family.id === 'streaming');
    groups.push({ ...streamingFamily, choices: [] });
  }

  return groups;
};

const isRMSNativeChoiceDisabled = (productScope, value) => {
  const variantSelects = productScope.querySelector('variant-selects');
  if (!variantSelects) return true;

  const radio = Array.from(variantSelects.querySelectorAll('input[type="radio"]'))
    .find((input) => input.value === value);
  if (radio) return radio.disabled || radio.classList.contains('disabled') || radio.getAttribute('aria-disabled') === 'true';

  const select = Array.from(variantSelects.querySelectorAll('select'))
    .find((element) => Array.from(element.options).some((option) => option.value === value));
  if (!select) return true;

  const option = Array.from(select.options).find((candidate) => candidate.value === value);
  return !option || option.disabled;
};

const selectRMSNativeChoice = (productScope, value) => {
  const variantSelects = productScope.querySelector('variant-selects');
  if (!variantSelects || isRMSNativeChoiceDisabled(productScope, value)) return false;

  const radio = Array.from(variantSelects.querySelectorAll('input[type="radio"]'))
    .find((input) => input.value === value);
  if (radio) {
    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  const select = Array.from(variantSelects.querySelectorAll('select'))
    .find((element) => Array.from(element.options).some((option) => option.value === value));
  if (!select) return false;

  select.value = value;
  select.dispatchEvent(new Event('change', { bubbles: true }));
  return true;
};

const setRMSStreamingMode = (productScope, enabled) => {
  productScope.dataset.rmsStreamingMode = enabled ? 'true' : 'false';
};

const renderRMSFormatPanel = (panel, group, productScope) => {
  if (!group) return;

  const copy = getRMSCopy();
  const { streamingRelease } = getRMSVariantData(productScope);
  setRMSStreamingMode(productScope, group.id === 'streaming');

  if (group.id === 'streaming') {
    const destination = `${RMS_STREAMING_BASE_URL}${encodeURIComponent(streamingRelease)}`;
    panel.innerHTML = `
      <div class="rms-format-selector__streaming">
        <p class="rms-format-selector__streaming-note">${escapeRMSHtml(copy.streamingNote)}</p>
        <a class="button button--secondary button--full-width rms-format-selector__streaming-link"
           href="${destination}"
           rel="noopener"
           aria-label="${escapeRMSHtml(copy.listen)}">
          ${escapeRMSHtml(copy.listen)}
        </a>
      </div>
    `;
    return;
  }

  panel.innerHTML = `
    <div class="rms-format-selector__options">
      ${group.choices.map((choice) => `
        <button class="rms-format-selector__option${choice.selected ? ' is-selected' : ''}${choice.unavailable ? ' is-unavailable' : ''}"
                type="button"
                aria-pressed="${choice.selected ? 'true' : 'false'}"
                ${choice.unavailable ? 'disabled aria-disabled="true"' : ''}
                data-rms-format-value="${escapeRMSHtml(choice.value)}">
          <span class="rms-format-selector__name">${escapeRMSHtml(getRMSFormatLabel(choice.value))}</span>
          <span class="rms-format-selector__meta">${escapeRMSHtml(getRMSFormatMeta(choice.value, choice.family))}</span>
          ${choice.price ? `<span class="rms-format-selector__price">${escapeRMSHtml(choice.price)}</span>` : ''}
          ${choice.unavailable ? `<span class="rms-format-selector__status">${escapeRMSHtml(copy.soldOut)}</span>` : ''}
        </button>
      `).join('')}
    </div>
  `;

  panel.querySelectorAll('[data-rms-format-value]:not(:disabled)').forEach((optionButton) => {
    optionButton.addEventListener('click', () => {
      const value = optionButton.dataset.rmsFormatValue;
      if (!selectRMSNativeChoice(productScope, value)) return;

      panel.querySelectorAll('[data-rms-format-value]').forEach((button) => {
        const isSelected = button === optionButton;
        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      });
    });
  });
};

const resetRMSFormatSelector = (productScope) => {
  const selector = productScope.querySelector('.rms-format-selector');
  if (selector) selector.remove();
  productScope.removeAttribute('data-rms-format-selector-active');
  productScope.removeAttribute('data-rms-streaming-mode');
};

const enhanceRMSFormatSelector = (scope = document) => {
  const productScopes = new Set(scope.querySelectorAll('product-info'));
  if (scope.matches && scope.matches('product-info')) productScopes.add(scope);

  productScopes.forEach((productScope) => {
    if (productScope.querySelector('.rms-format-selector')) return;

    const groups = buildRMSFormatGroups(productScope);
    if (!groups.length) {
      resetRMSFormatSelector(productScope);
      return;
    }

    const buttons = productScope.querySelector('.product-form__buttons');
    if (!buttons) return;

    const legacyStreamingCTA = productScope.querySelector('.rms-streaming-product-cta');
    if (legacyStreamingCTA) legacyStreamingCTA.remove();

    productScope.dataset.rmsFormatSelectorActive = 'true';
    setRMSStreamingMode(productScope, false);

    const selectedGroup = groups.find((group) => group.choices.some((choice) => choice.selected)) || groups[0];
    const showTabs = groups.length > 1;
    const purchaseGroups = groups.filter((group) => group.id !== 'streaming');
    const totalPurchaseChoices = purchaseGroups.reduce((count, group) => count + group.choices.length, 0);
    const copy = getRMSCopy();

    const selector = document.createElement('section');
    selector.className = [
      'rms-format-selector',
      !showTabs ? 'rms-format-selector--single-family' : '',
      !showTabs && totalPurchaseChoices === 1 ? 'rms-format-selector--single-choice' : ''
    ].filter(Boolean).join(' ');
    selector.setAttribute('aria-label', copy.availableFormats);
    selector.style.setProperty('--rms-format-columns', String(Math.min(groups.length, 4)));

    selector.innerHTML = `
      <p class="rms-format-selector__eyebrow">${escapeRMSHtml(!showTabs && totalPurchaseChoices === 1 ? copy.availableFormat : copy.availableFormats)}</p>
      ${showTabs ? `
        <div class="rms-format-selector__tabs" role="tablist" aria-label="${escapeRMSHtml(copy.availableFormats)}">
          ${groups.map((group) => `
            <button class="rms-format-selector__tab${group.id === selectedGroup.id ? ' is-active' : ''}"
                    type="button"
                    role="tab"
                    aria-selected="${group.id === selectedGroup.id ? 'true' : 'false'}"
                    data-rms-format-tab="${group.id}">
              ${escapeRMSHtml(group.label)}
            </button>
          `).join('')}
        </div>
      ` : ''}
      <div class="rms-format-selector__panel" role="tabpanel"></div>
    `;

    buttons.insertAdjacentElement('beforebegin', selector);

    const panel = selector.querySelector('.rms-format-selector__panel');
    renderRMSFormatPanel(panel, selectedGroup, productScope);

    selector.querySelectorAll('[data-rms-format-tab]').forEach((tab) => {
      tab.addEventListener('click', () => {
        const group = groups.find((candidate) => candidate.id === tab.dataset.rmsFormatTab);
        if (!group) return;

        selector.querySelectorAll('[data-rms-format-tab]').forEach((button) => {
          const isActive = button === tab;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        if (group.id !== 'streaming') {
          const currentChoices = getRMSNativeChoices(productScope);
          if (currentChoices) group.choices = currentChoices.filter((choice) => choice.family === group.id);
        }
        renderRMSFormatPanel(panel, group, productScope);
      });
    });
  });
};

const enhanceRMSProductPage = (scope = document) => {
  enhanceRMSProductDetails(scope);
  enhanceRMSFormatSelector(scope);
};

document.addEventListener('DOMContentLoaded', () => enhanceRMSProductPage());
document.addEventListener('shopify:section:load', (event) => enhanceRMSProductPage(event.target));
